import { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  NationalIdInput, TextInput, ToggleGroup, ChipSelector,
  Alert, Button, Skeleton, SkeletonButton, SkeletonRegion,
} from '@parsina/framework';
import PhoneInput from '../../../components/common/PhoneInput';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../../../i18n';
import { C } from '../../../constants/theme';
import { STACK } from '../../../constants/density.js';
import { bffQuery } from '../../../lib/bffClient.js';

/**
 * The two panes of «افزودن فرد» (docs/PEOPLE_PAGE_REDESIGN.md §8).
 *
 * They are two panes rather than two dialogs because the model says one thing
 * and the old UI said another. Adding a member of staff and inviting a
 * practitioner really are different acts — the clinic creates the first
 * relationship outright and can only offer the second
 * (PLATFORM_USER_CONNECTIONS §8) — but they answer the same question, «how do I
 * get another person into this clinic», and splitting them across a hand-rolled
 * dropdown meant the answer depended on a menu the phone never opened.
 *
 * Nothing here renders its own chrome. The dialog on desktop and the sheet on
 * a phone both mount these, which is what keeps the two surfaces from drifting
 * — the phone used to be missing the national id, the tier and the entire
 * practitioner path.
 */

const MY_CLINIC_CODE = 'query MyClinicInviteCode { myClinic { id name invitationCode } }';

/**
 * Add a member of staff.
 *
 * Controlled from the wrapper: the footer's submit button lives outside this
 * component, so the form's values cannot live inside it.
 */
export function StaffPane({
  value, onChange, roles = [], canAdmin, canSeedRoles,
  phoneError, formError, onShowExisting, onSeedRoles, onGoToRoles, seeding,
  phoneRef, nationalIdError,
}) {
  const { t } = useTranslation();
  const set = (patch) => onChange({ ...value, ...patch });
  const roleOptions = roles.map((r) => ({ value: r.id, label: r.nameFa }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* A failure belongs in front of the operator, next to the field that
          caused it — not in a toast behind a dialog they are still looking at. */}
      {formError && (
        <Alert
          variant="error"
          action={formError.existingId ? (
            <Button variant="outline" onClick={() => onShowExisting(formError.existingId)}>
              {t('people.add.showExisting')}
            </Button>
          ) : undefined}
        >
          {formError.message}
        </Alert>
      )}

      {/* These framework inputs self-label (they wrap FormField internally), so
          pass `label` directly — wrapping them in another FormField double-labels. */}
      {/* `PhoneInput` hands back E.164 and a `meta`, never an event. `phone` is
          the canonical value the submit sends; `phoneDraft` is what the operator
          has actually typed, and the dialog needs BOTH — a half-typed «۰۹۱۲»
          normalizes to `''`, so a gate reading `phone` alone would call a
          part-filled form empty and discard it without asking. */}
      <div ref={phoneRef}>
        <PhoneInput
          label={t('people.fields.phone')}
          error={phoneError}
          value={value.phone}
          onChange={(e164, meta) => set({ phone: e164, phoneDraft: meta.draft })}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <TextInput label={t('common.fields.name')} value={value.firstName}
          onChange={(e) => set({ firstName: e?.target ? e.target.value : e })} />
        <TextInput label={t('people.fields.lastName')} value={value.lastName}
          onChange={(e) => set({ lastName: e?.target ? e.target.value : e })} />
      </div>

      <NationalIdInput label={t('patients.fields.nationalIdOptional')} value={value.nationalId}
        onChange={(e) => set({ nationalId: e?.target ? e.target.value : e })}
        allowOtherCountries
        country={value.nationalIdCountry || undefined}
        onCountryChange={(c) => set({ nationalIdCountry: c })}
        error={nationalIdError || undefined} />

      {/* Only an admin may create an admin — the BFF refuses otherwise
          («فقط مدیر می‌تواند عضو مدیر اضافه کند»), and offering the choice to
          someone who will be refused is how a form teaches its own rules the
          hard way. */}
      {canAdmin ? (
        <ToggleGroup
          label={t('people.fields.tier')}
          value={value.tier}
          onChange={(v) => v && set({ tier: v })}
          options={[
            { value: 'MEMBER', label: t('people.tiers.member') },
            { value: 'ADMIN', label: t('people.tiers.admin') },
          ]}
        />
      ) : null}

      {value.tier === 'MEMBER' && (
        roles.length === 0 ? (
          <Alert
            variant="info"
            action={canSeedRoles ? (
              <Button variant="outline" disabled={seeding} onClick={onSeedRoles}>
                {t('people.add.seedRoles')}
              </Button>
            ) : (
              <Button variant="outline" onClick={onGoToRoles}>{t('people.add.goToRoles')}</Button>
            )}
          >
            {t('people.add.noRoles')}
          </Alert>
        ) : (
          <ChipSelector
            label={t('people.fields.roles')}
            multi
            value={value.roleIds}
            onChange={(v) => set({ roleIds: v })}
            options={roleOptions}
          />
        )
      )}

      {/* The truthful sentence — and it has changed, because the thing it
          describes did. `addStaffMember` no longer grants anything: it writes an
          INVITED row and nothing else, and the tier, the `clinic_id` and the
          composites are applied by `acceptClinicInvitation` when the person
          accepts. This copy used to say access was live immediately, which is
          what an admin then waited for while the invitation sat unanswered. */}
      <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.9 }}>
        {t('people.add.truth')}
      </div>
    </div>
  );
}

/**
 * Invite a practitioner — the clinic's invitation code, its link and its QR.
 *
 * A practitioner is a peer, not a hire: their professional identity is
 * platform-level and theirs (PLATFORM_USER_CONNECTIONS §8), so the clinic
 * cannot create it the way it creates a staff account. What it can do is hand
 * them a way in. Code, link and QR are three renderings of ONE value, so they
 * cannot disagree.
 *
 * The search half of the design is deliberately absent rather than faked:
 * `ListPractitioners` does not exist until Phase 2 of the service migration,
 * and a search box that cannot search is worse than one that is not there yet.
 */
export function InvitePane({ open }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open) { setCopied(null); return undefined; }
    let alive = true;
    setLoading(true); setError('');
    bffQuery(MY_CLINIC_CODE, {}, 'MyClinicInviteCode')
      .then((d) => { if (alive) setClinic(d?.myClinic || null); })
      .catch((e) => { if (alive) setError(e.message || t('errors.load.generic')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, t]);

  const code = clinic?.invitationCode || '';
  const link = code ? `${window.location.origin}/invite/${code}` : '';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    } catch {
      // A denied clipboard is not an error worth a toast — the value is on
      // screen and selectable, which is the fallback the operator already has.
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
      <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.9 }}>
        {t('people.invite.explainer')}
      </div>

      {loading && <InviteCodeSkeleton t={t} />}

      {!loading && error && <Alert variant="error">{error}</Alert>}

      {/* Naming the problem without the fix left this a dead end: the code is
          generated on «کلینیک من», one click away, and nothing here said so. */}
      {!loading && !error && !code && (
        <Alert
          variant="warning"
          action={(
            <Button variant="outline" onClick={() => navigate('/clinic?view=connect')}>
              {t('people.invite.generateHere')}
            </Button>
          )}
        >
          {t('people.invite.noCode')}
        </Alert>
      )}

      {!loading && !error && !!code && (
        <>
          <CopyRow label={t('people.invite.codeLabel')} value={code} mono
            copied={copied === 'code'} onCopy={() => copy(code, 'code')} t={t} />
          <CopyRow label={t('people.invite.linkLabel')} value={link}
            copied={copied === 'link'} onCopy={() => copy(link, 'link')} t={t} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            {/* Always on white with a quiet zone, whatever the panel theme:
                a scanner needs the contrast and the margin, and this is the
                one surface where the brand palette must not win. */}
            <div style={{
              background: '#fff', padding: 12, borderRadius: 12,
              border: `1px solid ${C.border}`, lineHeight: 0,
            }}>
              <QRCodeSVG value={link} size={168} level="M" marginSize={2}
                bgColor="#ffffff" fgColor="#000000" title={t('people.invite.qrTitle')} />
            </div>
            <div style={{ fontSize: 12.5, color: C.textMuted, textAlign: 'center', lineHeight: 1.8 }}>
              {t('people.invite.qrHint')}
            </div>
          </div>

          {canShare && (
            <Button variant="outline" onClick={() => navigator.share({ url: link }).catch(() => {})}>
              {t('people.invite.share')}
            </Button>
          )}

          {/* Closes the loop: the manager who hands over a code should know
              where the answer will appear, and that it needs their approval. */}
          <Alert variant="info">{t('people.invite.afterScan')}</Alert>
        </>
      )}

      <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.8 }}>
        {t('people.invite.searchComingSoon')}
      </div>
    </div>
  );
}

/**
 * The code panel's own shape while the invitation code loads: the two copy
 * rows, the QR on its white ground, the hint under it — so the panel does not
 * grow from a spinner into a card when the code lands.
 */
function InviteCodeSkeleton({ t }) {
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="invite-code-skeleton"
      style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
      {[0, 1].map((i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width={i === 0 ? 72 : 96} height={13} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Skeleton width="100%" height={41} radius={10} style={{ flex: 1 }} />
            <SkeletonButton width={88} />
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <Skeleton width={194} height={194} radius={12} />
        <Skeleton width={220} height={13} />
      </div>
      <Skeleton width="100%" height={48} radius={10} />
    </SkeletonRegion>
  );
}

function CopyRow({ label, value, copied, onCopy, t, mono = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12.5, color: C.textSecondary }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          dir="ltr"
          style={{
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            unicodeBidi: 'isolate', textAlign: 'start',
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: '10px 12px', fontSize: mono ? 15 : 12.5,
            fontWeight: mono ? 800 : 500, letterSpacing: mono ? 1 : 0, color: C.textPrimary,
          }}
        >
          {value}
        </div>
        <Button variant="outline" onClick={onCopy}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? t('people.invite.copied') : t('people.invite.copy')}
        </Button>
      </div>
    </div>
  );
}
